// A guest tells the host's QR "someone connected" — including a known guest re-scanning.
//
// A guest who already met this host re-enters through the tab's stored session, so
// their room is the OLD conversation, while the host's live QR session points at a
// fresh room. Marking the old room's session matched nothing (ZERO_ROWS) and the host
// stayed on its QR, never told (invite-and-rescan-display-bugs §2). The live room is
// known on every /u/ open (index.html's prefetch). When it differs from the guest's
// room, mark_guest_returned tells the LIVE session which room the two already share;
// the server checks the caller really is that room's guest and the session is that
// room's host. The host then moves to the existing conversation (qrDetection).
//
// Permanent data layer: no React.

import { markAnonConnected, postScannerRpc } from './activeSession';
import { logConnectionError } from './analytics/eventLogger';

/** The host's live QR room from index.html's prefetch, or null (no live session). */
const liveRoomFromPrefetch = async () => {
  if (typeof window === 'undefined' || !window.__PREFETCH__) return null;
  try {
    const data = await window.__PREFETCH__;
    return data?.chatRoomId || null;
  } catch {
    return null;
  }
};

const markGuestReturned = async (liveRoom, existingRoom) => {
  const result = await postScannerRpc(
    'mark_guest_returned',
    { p_live_room: liveRoom, p_existing_room: existingRoom },
    { operation: 'mark_guest_returned', chatRoomId: existingRoom }
  );
  return result.ok ? result.data : null; // 'marked' | 'no_live_session' | 'not_your_room'
};

/**
 * Signal the host that this guest has connected.
 * @param {string} chatRoomId - the room this guest is in
 * @returns {Promise<boolean>} whether a live host session was told
 */
export const signalHostOfGuest = async (chatRoomId) => {
  const liveRoom = await liveRoomFromPrefetch();
  // A first scan (or a refresh mid-exchange) sits in the live room itself: today's mark
  if (!liveRoom || liveRoom === chatRoomId) return markAnonConnected(chatRoomId);

  const result = await markGuestReturned(liveRoom, chatRoomId);
  if (result === 'marked') return true;
  if (result === 'not_your_room') {
    // The tab holds a room from a different host — not a known guest of this one
    logConnectionError('guest_return_not_your_room', { chatRoomId, liveRoom });
  }
  return false;
};
